<template>
    <div
        class="fixed !z-[9998] inset-0 overflow-y-auto"
        @wheel="handleScroll"
        @touchmove="handleScroll"
        @scroll="handleScroll"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
    >
        <div
            class="flex items-end justify-center min-h-screen text-center sm:block sm:p-0"
            :class="[removePadding ? '': 'pt-4 px-4 pb-20']"
        >
            <!--
            Background overlay, show/hide based on modal state.

            Entering: "ease-out duration-300"
                From: "opacity-0"
                To: "opacity-100"
            Leaving: "ease-in duration-200"
                From: "opacity-100"
                To: "opacity-0"
            -->
            <transition
                enter-active-class="ease-out duration-300"
                leave-active-class="ease-in duration-200"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    class="fixed inset-0 transition-opacity"
                    aria-hidden="true"
                    style="background: rgba(0, 0, 0, 0.5); opacity: 0.4"
                ></div>
            </transition>

            <!-- This element is to trick the browser into centering the modal contents. -->
            <span
                class="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
                >&#8203;</span
            >

            <!--
            Modal panel, show/hide based on modal state.

            Entering: "ease-out duration-300"
                From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                To: "opacity-100 translate-y-0 sm:scale-100"
            Leaving: "ease-in duration-200"
                From: "opacity-100 translate-y-0 sm:scale-100"
                To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            -->
            <transition
                enter-active-class="ease-out duration-300"
                leave-active-class="ease-in duration-200"
                enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                leave-class="opacity-100 translate-y-0 sm:scale-100"
                leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
                <div
                    :class="[cssWidth, cssRoundedSize, 'inline-block relative align-bottom bg-white text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full']"
                >
                    <div v-if="isNewMark" class="absolute mt-[10px] top-0  w-full flex justify-center items-center">
                        <div class="newLabel w-[101px] text-center">
                            <span class="font-[Inter] text-[#FFFFFF] text-sm font-semibold px-7">
                                New
                            </span>
                        </div>
                    </div>
                    <div class="flex justify-end" :class="[removePadding ? '': 'px-4 pt-4']">
                        <span
                            v-if="allowClose"
                            @click="closeModal"
                        >
                            <XMarkIcon
                               class="size-6 text-won-dark-blue cursor-pointer"
                            />
                        </span>
                    </div>
                    <div class="bg-white" :class="[removePadding ? '': 'px-4 pb-4 sm:pb-4']">
                        <div class="sm:text-left" :class="[removePadding ? '': 'mt-3 sm:mt-0 sm:mx-4 ']">
                            <slot name="header"></slot>
                            <div :class="[removePadding ? '': 'mt-5']">
                                <slot name="body" />
                            </div>
                        </div>
                    </div>
                    <div v-if="!hideActions" class="px-6 py-6 flex flex-col space-y-2 sm:flex sm:flex-row sm:space-y-0 sm:justify-between">
                        <slot name="actions" />
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { onMounted, computed, onBeforeUnmount, toRefs, watch } from 'vue';

import { XMarkIcon } from '@heroicons/vue/24/outline';

const emit = defineEmits(["modal:close"])
const props = defineProps({
    isNewMark: {
        type: Boolean,
        default: false,
    },
    preventScrolling: {
        type: Boolean,
        default: true,
    },
    hideActions: {
        type: Boolean,
        default: false,
    },
    allowClose: {
        type: Boolean,
        default: true,
    },
    maxWidth: {
        type: String,
        default: null,
    },
    roundedSize: {
        type: String,
        default: undefined,
    },
    removePadding: {
        type: Boolean,
        default: false
    },
    closeIconColor: {
        type: String,
        default: 'black',
    }
})

const {
    isNewMark,
    preventScrolling,
    hideActions,
    allowClose,
    maxWidth,
    roundedSize,
    removePadding
} = toRefs(props)

const closeModal = () => emit("modal:close");

const handleEscape = (e) => {
    if (e.keyCode === 27 && allowClose.value) {
        closeModal();
    }
}

onMounted(() => {
    document.addEventListener("keydown", handleEscape);
})

onBeforeUnmount(() => {
    document.removeEventListener("keydown", handleEscape);
})

const handleScroll = e => {
    if (preventScrolling.value) {
        e.preventDefault()
        return false;
    }
}

const cssWidth = computed(() => {
    return maxWidth.value ?? "sm:max-w-md"
})

watch(maxWidth, (newValue) => {
    cssWidth.value = newValue
})

const cssRoundedSize = computed(() => {
    return (roundedSize.value ?? "rounded-xl");
})
</script>

<style scoped>
  .newLabel {
    background: linear-gradient(180deg, #073B5D 0%, #2A8176 100%);
    border-radius: 44px;
  }
</style>
